import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Save, Loader2, Settings2, MessageSquare, Code2, FlaskConical,
  DollarSign, Clock, Phone, Mail, Plus, Trash2, Edit3, Check, X,
  Database, Tag, Globe, ExternalLink, CheckCircle2,
  AlertCircle, Copy, Users, UserPlus, Sparkles, Zap, KeyRound,
  Activity, RefreshCw, Cpu, Brain, Palette, Wand2,
} from "lucide-react";
import { authFetch, API } from "../utils/api";

const PROMPT_VARS = [
  // ⭐ Mega-variable: dumps ALL biz + contact + research data in one block
  { key: "{{datos_contacto}}", desc: "⭐ TODO sobre el negocio + contacto + investigación (bloque estructurado)" },
  // ─ Negocio (Business) ───────────────────────────────────────────────
  { key: "{{nombre}}", desc: "Nombre del negocio" },
  { key: "{{categoria}}", desc: "Categoría/industria" },
  { key: "{{telefono}}", desc: "Teléfono" },
  { key: "{{email}}", desc: "Email del negocio" },
  { key: "{{direccion}}", desc: "Dirección" },
  { key: "{{website}}", desc: "Sitio web actual" },
  { key: "{{rating}}", desc: "Rating de Google Maps" },
  // ─ Contacto (CRM) ───────────────────────────────────────────────────
  { key: "{{empresa}}", desc: "Empresa del contacto (o nombre del negocio)" },
  { key: "{{nombre_contacto}}", desc: "Nombre del contacto humano" },
  { key: "{{fuente}}", desc: "Origen del contacto (manual/webhook/maps)" },
  { key: "{{tags}}", desc: "Etiquetas del contacto (coma-separadas)" },
  { key: "{{notas}}", desc: "Notas internas del contacto (últimas 10)" },
  { key: "{{seguimiento}}", desc: "Fecha del próximo seguimiento" },
  { key: "{{seguimiento_notas}}", desc: "Notas del próximo seguimiento" },
  // ─ Custom fields (dinámicos) ────────────────────────────────────────
  { key: "{{campo.tu_id}}", desc: "Campo personalizado (reemplazá tu_id por el ID real, se configura en Campos de negocio)" },
  // ─ Investigación + Script (se inyectan al generar sitio / outreach) ─
  { key: "{{investigacion}}", desc: "Resumen de investigación IA" },
  { key: "{{propuesta_valor}}", desc: "Propuesta de valor" },
  { key: "{{servicios}}", desc: "Lista de servicios" },
  { key: "{{audiencia}}", desc: "Público objetivo" },
  { key: "{{pain_points}}", desc: "Puntos de dolor" },
  { key: "{{script_web}}", desc: "Script persuasivo completo" },
  { key: "{{titular}}", desc: "Titular del hero" },
  { key: "{{subtitular}}", desc: "Subtitular del hero" },
  { key: "{{cta}}", desc: "Texto del CTA principal" },
];

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "email", label: "Email" },
  { value: "date", label: "Fecha" },
  { value: "multivalue", label: "MultiValue" },
];

const VISIBILITY_OPTIONS = [
  { value: "always", label: "Mostrar siempre" },
  { value: "hide_empty", label: "Ocultar cuando esté vacío" },
  { value: "never", label: "Ocultar siempre" },
];

// Prompts siempre primero (protagonistas), campos pequeños después en grid.
const FIELDS = [
  { key: "site_generation_prompt", label: "Prompt: Generación de Sitios", icon: <Code2 size={15} />, type: "textarea", placeholder: "Instrucciones para la IA al generar sitios web...\n\nPuedes usar {{nombre}}, {{investigacion}}, {{script_web}}, etc.", hint: "Describe el estilo, tono y características. Usa variables para personalizar.", hasVars: true },
  { key: "research_prompt", label: "Prompt: Investigación de Empresas", icon: <FlaskConical size={15} />, type: "textarea", placeholder: "Instrucciones para investigar empresas...\n\nEjemplo: Analizá {{empresa}} (rubro {{categoria}}). Tené en cuenta: {{notas}}. LinkedIn: {{campo.linkedin}}.", hint: "Variables disponibles abajo. Campos personalizados se gestionan en 'Campos de negocio' y se usan como {{campo.id}}.", hasVars: true },
  { key: "outreach_prompt", label: "Prompt: Mensajes de Outreach", icon: <MessageSquare size={15} />, type: "textarea", placeholder: "Instrucciones para generar emails y mensajes...", hint: "Tono, estilo y enfoque de los mensajes personalizados.", hasVars: true },
  { key: "whatsapp_number", label: "Número de WhatsApp", icon: <Phone size={15} />, type: "input", placeholder: "59897401287", hint: "Sin espacios ni guiones. Incluir código de país.", hasVars: false },
  { key: "offer_price", label: "Precio de la oferta", icon: <DollarSign size={15} />, type: "currency-price", placeholder: "50", hint: "Precio mensual en la página de preview.", hasVars: false },
  { key: "countdown_hours", label: "Horas de cuenta regresiva", icon: <Clock size={15} />, type: "number", placeholder: "12", hint: "Duración de la oferta después de generar el sitio.", hasVars: false },
  { key: "sender_email", label: "Email del remitente (Resend)", icon: <Mail size={15} />, type: "input", placeholder: "onboarding@resend.dev", hint: "Email verificado en Resend.", hasVars: false },
];

// URL pública de producción (webhook expuesto al exterior)
const PRODUCTION_URL = "https://sitiosya.com.uy";

const TABS = [
  { id: "prompts", label: "Prompts", icon: <Settings2 size={14} /> },
  { id: "fields", label: "Campos", icon: <Database size={14} /> },
  { id: "ai", label: "IA & Sistema", icon: <Cpu size={14} /> },
  { id: "domain", label: "Dominio", icon: <Globe size={14} /> },
  { id: "client-domains", label: "Dominios clientes", icon: <Globe size={14} /> },
  { id: "users", label: "Usuarios", icon: <Users size={14} /> },
];

// ── Design tokens (Digital Architect) ────────────────────────────────────────
const ARCHITECT = {
  surface: "#0e0e0e",
  surfaceLow: "#131313",
  surfaceContainer: "#1a1a1a",
  surfaceHigh: "#262626",
  primary: "#FFC107",
  primaryDim: "#ffd16c",
  primaryContainer: "#fdc003",
  onSurface: "#f5f5f4",
  onSurfaceVariant: "#adaaaa",
  ghost: "rgba(255,255,255,0.08)",
};

// Reusable editorial card shell
function BentoCard({ children, className = "", style = {} }) {
  return (
    <div
      className={`rounded-2xl relative overflow-hidden transition-all ${className}`}
      style={{
        background: ARCHITECT.surfaceContainer,
        border: `1px solid ${ARCHITECT.ghost}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SectionKicker({ children }) {
  return (
    <span
      className="font-semibold tracking-[0.3em] text-[10px] uppercase block mb-3"
      style={{ color: ARCHITECT.primary, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
    >
      {children}
    </span>
  );
}

export default function SettingsPage() {
  const [tab, setTab] = useState("prompts");
  const [settings, setSettings] = useState({});
  const lastFocusedFieldRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showVars, setShowVars] = useState({});

  useEffect(() => {
    authFetch(`${API}/settings`)
      .then(r => r && r.json())
      .then(d => { if (d) { setSettings(d); setLoading(false); } })
      .catch((err) => { console.error("[Settings] Load failed:", err); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/settings`, { method: "PUT", body: JSON.stringify(settings) });
      if (!res || !res.ok) throw new Error();
      toast.success("Configuración guardada");
    } catch (err) { console.error("[settings] save failed:", err); toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const insertVar = (key, varKey) => {
    // Insert at the caret position of the matching textarea if possible,
    // otherwise append at the end. Much more natural than always appending.
    setSettings(s => {
      const el = document.querySelector(`[data-testid="settings-${key}"]`);
      const current = s[key] || "";
      if (el && typeof el.selectionStart === "number" && document.activeElement === el) {
        const start = el.selectionStart;
        const end   = el.selectionEnd;
        const next  = current.slice(0, start) + varKey + current.slice(end);
        // Restore cursor AFTER the inserted token on next tick
        setTimeout(() => {
          try {
            el.focus();
            const pos = start + varKey.length;
            el.setSelectionRange(pos, pos);
          } catch (caretErr) { console.debug("[settings] caret restore failed:", caretErr?.message); }
        }, 0);
        return { ...s, [key]: next };
      }
      return { ...s, [key]: current + varKey };
    });
  };

  // Insert into whichever prompt textarea was last focused (or fallback: copy).
  const insertIntoFocusedOrCopy = (varKey) => {
    const last = lastFocusedFieldRef.current;
    if (last && FIELDS.some(f => f.key === last && f.hasVars)) {
      insertVar(last, varKey);
      toast.success(`${varKey} insertado en ${FIELDS.find(f => f.key === last).label}`);
    } else {
      // Fallback: copy to clipboard
      // (uses the same robust fallback as `copy()` in PromptsSection)
      const fallback = () => {
        try {
          const ta = document.createElement("textarea");
          ta.value = varKey;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          ta.remove();
        } catch (clipErr) { console.debug("[settings] clipboard fallback failed:", clipErr?.message); }
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(varKey).catch(fallback);
      } else {
        fallback();
      }
      toast(`${varKey} copiado`, { description: "Click dentro de un prompt para insertar en lugar de copiar" });
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen" style={{ background: ARCHITECT.surface }}>
      <Loader2 className="animate-spin" size={32} style={{ color: ARCHITECT.primary }} />
    </div>
  );

  return (
    <div
      className="min-h-screen pb-24"
      style={{
        background: ARCHITECT.surface,
        color: ARCHITECT.onSurface,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {/* ── Editorial Header ─────────────────────────────────────── */}
      <header
        className="sticky top-0 z-30"
        style={{
          background: "rgba(14,14,14,0.75)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderBottom: `1px solid ${ARCHITECT.ghost}`,
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `${ARCHITECT.primary}12`, border: `1px solid ${ARCHITECT.primary}25` }}
            >
              <Settings2 size={18} style={{ color: ARCHITECT.primary }} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: ARCHITECT.onSurfaceVariant }}>
                Sistema · Elite
              </p>
              <h1
                className="text-xl md:text-2xl font-black tracking-tight"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Panel de Configuración
              </h1>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: `${ARCHITECT.primary}10`, color: ARCHITECT.primary }}
            >
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: ARCHITECT.primary }} />
              Motor estable
            </div>
          </div>
        </div>
      </header>

      {/* ── Editorial Hook ────────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-8">
        <SectionKicker>Sistema Elite</SectionKicker>
        <h2
          className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-5 leading-[1.05]"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          Configuración del <span style={{ color: ARCHITECT.primary }}>Arquitecto</span>
        </h2>
        <p className="text-base md:text-lg max-w-2xl leading-relaxed" style={{ color: ARCHITECT.onSurfaceVariant }}>
          Personaliza tu ecosistema CRM. Define la identidad, gestiona el flujo de trabajo y optimiza las conexiones de API para un rendimiento superior.
        </p>
      </section>

      {/* ── Tabs (Pill Navigation) ────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-8">
        <div
          className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide p-1.5 rounded-2xl w-fit max-w-full"
          style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
        >
          {TABS.map(t => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={`settings-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-xl transition-all whitespace-nowrap"
                style={active
                  ? {
                      background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`,
                      color: "#0e0e0e",
                      boxShadow: `0 8px 24px -8px ${ARCHITECT.primary}55`,
                    }
                  : { color: ARCHITECT.onSurfaceVariant }}
              >
                {t.icon} {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Tab Content ───────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {tab === "prompts" && (
          <PromptsSection
            settings={settings}
            setSettings={setSettings}
            showVars={showVars}
            setShowVars={setShowVars}
            insertVar={insertVar}
            insertIntoFocusedOrCopy={insertIntoFocusedOrCopy}
            lastFocusedFieldRef={lastFocusedFieldRef}
            saving={saving}
            handleSave={handleSave}
          />
        )}
        {tab === "fields" && <ContactFieldsSection />}
        {tab === "ai" && <AIAndSystemSection />}
        {tab === "domain" && <DomainSection />}
        {tab === "client-domains" && <ClientDomainsSection />}
        {tab === "users" && <UsersSection />}
      </div>
    </div>
  );
}

// ── Prompts Section (Bento Layout) ────────────────────────────────────────────

function PromptsSection({ settings, setSettings, showVars, setShowVars, insertVar, insertIntoFocusedOrCopy, lastFocusedFieldRef, saving, handleSave }) {
  const webhookUrl = `${PRODUCTION_URL}/api/webhook/businesses`;
  const copy = (text, label = "Copiado al portapapeles") => {
    // Clipboard API puede fallar en iframes / contextos sin permisos.
    // Fallback a textarea + execCommand para máxima compatibilidad.
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success(label);
      } catch {
        toast.error("No se pudo copiar");
      }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(label)).catch(fallback);
    } else {
      fallback();
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4 md:gap-6 pb-8">
      {/* Webhook — Compact inline bar (reduced vertical space) */}
      <div
        data-testid="webhook-bar"
        className="col-span-12 flex items-center gap-3 px-4 py-2.5 rounded-xl"
        style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${ARCHITECT.primary}14` }}
        >
          <Zap size={14} style={{ color: ARCHITECT.primary }} />
        </div>
        <div className="flex-shrink-0 min-w-0 hidden sm:block">
          <p className="text-xs font-bold leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Webhook de ingestas
          </p>
          <p className="text-[10px] leading-tight" style={{ color: ARCHITECT.onSurfaceVariant }}>
            POST — Google Maps / n8n / Zapier
          </p>
        </div>
        <code
          data-testid="webhook-url"
          className="text-[11px] md:text-xs font-mono flex-1 truncate px-2 py-1.5 rounded-md"
          style={{ color: ARCHITECT.primary, background: ARCHITECT.surfaceHigh }}
          title={webhookUrl}
        >
          {webhookUrl}
        </code>
        <button
          data-testid="webhook-copy-btn"
          onClick={() => copy(webhookUrl)}
          title="Copiar URL"
          className="flex-shrink-0 px-2.5 py-1.5 rounded-md transition-colors flex items-center gap-1.5 text-[11px] font-semibold"
          style={{ color: ARCHITECT.onSurfaceVariant, background: ARCHITECT.surfaceHigh }}
          onMouseEnter={e => { e.currentTarget.style.color = ARCHITECT.primary; }}
          onMouseLeave={e => { e.currentTarget.style.color = ARCHITECT.onSurfaceVariant; }}
        >
          <Copy size={12} />
          <span className="hidden md:inline">Copiar</span>
        </button>
      </div>

      {/* Variables reference — Full-width chips card */}
      <BentoCard className="col-span-12 p-6 md:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${ARCHITECT.primary}12` }}
          >
            <Tag size={16} style={{ color: ARCHITECT.primary }} />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Variables
            </h3>
            <p className="text-[11px]" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Click en un prompt y luego en una variable → se inserta en el cursor
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PROMPT_VARS.map(v => (
            <button
              key={v.key}
              type="button"
              data-testid={`var-chip-${v.key.replace(/[{}]/g, "")}`}
              onMouseDown={(e) => e.preventDefault()} /* keep textarea focus */
              onClick={() => insertIntoFocusedOrCopy(v.key)}
              title={v.desc}
              className="text-[10px] font-mono px-2 py-1 rounded-md font-semibold transition-all cursor-pointer"
              style={{ background: ARCHITECT.surfaceLow, color: ARCHITECT.primary, border: `1px solid ${ARCHITECT.ghost}` }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${ARCHITECT.primary}15`;
                e.currentTarget.style.borderColor = `${ARCHITECT.primary}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = ARCHITECT.surfaceLow;
                e.currentTarget.style.borderColor = ARCHITECT.ghost;
              }}
            >
              {v.key}
            </button>
          ))}
        </div>
      </BentoCard>

      {/* Prompt & config fields — textareas full width, inputs en grid de 3 */}
      {FIELDS.map((field, idx) => {
        const isTextarea = field.type === "textarea";
        const span = isTextarea
          ? "col-span-12"
          : "col-span-12 sm:col-span-6 lg:col-span-4";
        return (
          <BentoCard key={field.key} className={`${span} p-5 md:p-6 flex flex-col`}>
            <div className="flex items-start gap-3 mb-4">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${ARCHITECT.primary}10` }}
              >
                <span style={{ color: ARCHITECT.primary }}>{field.icon}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: ARCHITECT.primary }}>
                  Módulo {String(idx + 1).padStart(2, "0")}
                </p>
                <h4
                  className="text-sm md:text-base font-bold mt-0.5 leading-tight"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {field.label}
                </h4>
              </div>
            </div>
            {field.hint && (
              <p className="text-[11px] md:text-xs mb-3 leading-relaxed" style={{ color: ARCHITECT.onSurfaceVariant }}>
                {field.hint}
              </p>
            )}

            {isTextarea ? (
              <>
                <textarea
                  data-testid={`settings-${field.key}`}
                  value={settings[field.key] || ""}
                  onChange={e => setSettings(s => ({ ...s, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  rows={6}
                  className="w-full rounded-xl px-4 py-3 text-[13px] leading-relaxed resize-y font-mono transition-all focus:outline-none min-h-[160px]"
                  style={{
                    background: ARCHITECT.surfaceLow,
                    border: `1px solid ${ARCHITECT.ghost}`,
                    color: ARCHITECT.onSurface,
                  }}
                  onFocus={(e) => { e.target.style.borderColor = `${ARCHITECT.primary}55`; if (lastFocusedFieldRef) lastFocusedFieldRef.current = field.key; }}
                  onBlur={(e) => { e.target.style.borderColor = ARCHITECT.ghost; }}
                />
                {field.hasVars && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setShowVars(v => ({ ...v, [field.key]: !v[field.key] }))}
                      className="text-xs flex items-center gap-1.5 font-bold hover:underline"
                      style={{ color: ARCHITECT.primary }}
                    >
                      <Tag size={11} /> Insertar variable {showVars[field.key] ? "▲" : "▼"}
                    </button>
                    {showVars[field.key] && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {PROMPT_VARS.map(v => (
                          <button
                            key={v.key}
                            type="button"
                            data-testid={`inline-var-${field.key}-${v.key.replace(/[{}.]/g, "")}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              const el = document.querySelector(`[data-testid="settings-${field.key}"]`);
                              if (el) { el.focus(); if (lastFocusedFieldRef) lastFocusedFieldRef.current = field.key; }
                            }}
                            onClick={() => insertVar(field.key, v.key)}
                            title={v.desc}
                            className="text-[10px] font-mono px-2.5 py-1.5 rounded-lg font-semibold transition-all"
                            style={{
                              background: ARCHITECT.surfaceLow,
                              color: ARCHITECT.onSurfaceVariant,
                              border: `1px solid ${ARCHITECT.ghost}`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = `${ARCHITECT.primary}15`;
                              e.currentTarget.style.color = ARCHITECT.primary;
                              e.currentTarget.style.borderColor = `${ARCHITECT.primary}40`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = ARCHITECT.surfaceLow;
                              e.currentTarget.style.color = ARCHITECT.onSurfaceVariant;
                              e.currentTarget.style.borderColor = ARCHITECT.ghost;
                            }}
                          >
                            {v.key}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : field.type === "currency-price" ? (
              <div className="mt-auto">
                <div className="flex gap-2">
                  <select
                    data-testid="settings-offer_currency"
                    value={settings.offer_currency || "USD"}
                    onChange={e => setSettings(s => ({ ...s, offer_currency: e.target.value }))}
                    className="rounded-xl px-3 py-2.5 text-sm transition-all appearance-none cursor-pointer focus:outline-none min-w-0"
                    style={{
                      background: ARCHITECT.surfaceLow,
                      border: `1px solid ${ARCHITECT.ghost}`,
                      color: ARCHITECT.onSurface,
                      maxWidth: "110px",
                    }}
                  >
                    <option value="USD">🇺🇸 USD</option>
                    <option value="UYU">🇺🇾 UYU</option>
                    <option value="EUR">🇪🇺 EUR</option>
                  </select>
                  <input
                    data-testid="settings-offer_price"
                    type="number"
                    min="1"
                    value={settings.offer_price || ""}
                    onChange={e => setSettings(s => ({ ...s, offer_price: Number(e.target.value) }))}
                    placeholder="50"
                    className="flex-1 rounded-xl px-3 py-2.5 text-sm transition-all focus:outline-none min-w-0"
                    style={{
                      background: ARCHITECT.surfaceLow,
                      border: `1px solid ${ARCHITECT.ghost}`,
                      color: ARCHITECT.onSurface,
                    }}
                  />
                </div>
                <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px]" style={{ color: ARCHITECT.onSurfaceVariant }}>Vista previa:</span>
                  <span
                    className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md"
                    style={{ background: `${ARCHITECT.primary}15`, color: ARCHITECT.primary, border: `1px solid ${ARCHITECT.primary}25` }}
                  >
                    {(() => {
                      const p = settings.offer_price || 0;
                      const c = settings.offer_currency || "USD";
                      if (c === "UYU") return `$U ${Math.round(p).toLocaleString("es-UY")}/mes`;
                      if (c === "EUR") return `€${p}/mes`;
                      return `$${p}/mes`;
                    })()}
                  </span>
                </div>
              </div>
            ) : (
              <input
                data-testid={`settings-${field.key}`}
                type={field.type}
                value={settings[field.key] || ""}
                onChange={e => setSettings(s => ({ ...s, [field.key]: field.type === "number" ? Number(e.target.value) : e.target.value }))}
                placeholder={field.placeholder}
                className="w-full rounded-xl px-3 py-2.5 text-sm transition-all focus:outline-none mt-auto"
                style={{
                  background: ARCHITECT.surfaceLow,
                  border: `1px solid ${ARCHITECT.ghost}`,
                  color: ARCHITECT.onSurface,
                }}
              />
            )}
          </BentoCard>
        );
      })}

      {/* Signature Element — Performance Insight con datos reales */}
      <PerformanceInsight saving={saving} handleSave={handleSave} />
    </div>
  );
}

// ── Performance Insight Card ─────────────────────────────────────────────────

function Sparkline({ income, expense, primary }) {
  const maxVal = Math.max(...income, ...expense, 1);
  const w = 260;
  const h = 60;
  const pad = 4;
  const dx = (w - pad * 2) / Math.max(income.length - 1, 1);
  const toY = (v) => pad + (h - pad * 2) * (1 - v / maxVal);

  const incomePts = income.map((v, i) => `${pad + i * dx},${toY(v)}`).join(" ");
  const expensePts = expense.map((v, i) => `${pad + i * dx},${toY(v)}`).join(" ");

  const areaPath = `M ${pad},${h - pad} L ${incomePts.replace(/,/g, " ").split(" ").map((p, i) => i % 2 === 0 ? p : p).join(" ")} L ${w - pad},${h - pad} Z`;

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={primary} stopOpacity="0.25" />
          <stop offset="100%" stopColor={primary} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Income area */}
      <polygon
        points={`${pad},${h - pad} ${incomePts} ${w - pad},${h - pad}`}
        fill="url(#sparkFill)"
      />
      {/* Income line */}
      <polyline
        points={incomePts}
        fill="none"
        stroke={primary}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* Expense line (dashed, red) */}
      <polyline
        points={expensePts}
        fill="none"
        stroke="#ff7351"
        strokeWidth="1.5"
        strokeDasharray="3 3"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity="0.6"
      />
      {/* Dots on last point */}
      {income.length > 0 && (
        <circle
          cx={pad + (income.length - 1) * dx}
          cy={toY(income[income.length - 1])}
          r="3"
          fill={primary}
        />
      )}
    </svg>
  );
}

function PerformanceInsight({ saving, handleSave }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMetrics = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await authFetch(`${API}/financial/summary`);
      if (res?.ok) {
        const d = await res.json();
        setMetrics(d);
        setLastUpdated(new Date());
      }
    } catch (err) { console.warn("[settings] financial/summary fetch failed:", err?.message); }
    finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics(true);
    const interval = setInterval(() => fetchMetrics(true), 30000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const latency = metrics?.health?.latency_ms ?? null;
  const latencyStatus = latency === null ? "—" : latency < 50 ? "Estable" : latency < 200 ? "Normal" : "Alto";
  const latencyColor = latency === null ? ARCHITECT.onSurfaceVariant : latency < 50 ? ARCHITECT.primary : latency < 200 ? "#eab308" : "#ff7351";

  const marginUsd = metrics?.finance?.margin_usd ?? 0;
  const totalLeads = metrics?.crm?.total_leads ?? 0;
  const wonLeads = metrics?.crm?.won_leads ?? 0;
  const convRate = metrics?.crm?.conversion_rate ?? 0;
  const customDomains = metrics?.platform?.custom_domains ?? 0;
  const recentPayments = metrics?.health?.recent_payments_30d ?? 0;
  const dailyIncome = metrics?.finance?.daily_income_7d ?? [];
  const dailyExpense = metrics?.finance?.daily_expense_7d ?? [];
  const incomeWeekTotal = dailyIncome.reduce((a, b) => a + b, 0);

  const fmtUSD = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}K` : `$${Math.round(n)}`;
  const fmtLastUpdated = () => {
    if (!lastUpdated) return "—";
    const diff = Math.round((Date.now() - lastUpdated.getTime()) / 1000);
    if (diff < 5) return "ahora";
    if (diff < 60) return `hace ${diff}s`;
    return `hace ${Math.round(diff / 60)}m`;
  };

  // Heuristic engine capacity: fn of latency + recent activity
  const capacity = Math.min(99, Math.max(70,
    100 - (latency !== null ? Math.min(30, latency / 10) : 15)
  ));

  return (
    <div
      data-testid="performance-insight-card"
      className="col-span-12 mt-8 rounded-3xl p-8 md:p-12 relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${ARCHITECT.primary}08, transparent 60%)`,
        border: `1px solid ${ARCHITECT.primary}15`,
      }}
    >
      <div
        className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: `${ARCHITECT.primary}10`, filter: "blur(100px)" }}
      />

      {/* Live indicator (top-right corner) */}
      <div className="absolute top-5 right-5 flex items-center gap-2 z-20" data-testid="live-indicator">
        <button
          onClick={() => fetchMetrics()}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all hover:brightness-110 disabled:opacity-60"
          style={{ background: ARCHITECT.surfaceLow, color: ARCHITECT.onSurfaceVariant, border: `1px solid ${ARCHITECT.ghost}` }}
          title="Actualizar ahora (auto cada 30s)"
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${refreshing ? "animate-ping" : "animate-pulse"}`}
            style={{ background: refreshing ? ARCHITECT.primary : "#22c55e" }}
          />
          {refreshing ? "Sync..." : `Live · ${fmtLastUpdated()}`}
        </button>
      </div>

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div>
          <SectionKicker>Performance Insight</SectionKicker>
          <h3
            className="text-3xl md:text-4xl font-black mb-4 tracking-tight leading-tight"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Optimización del Motor CRM
          </h3>
          <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: ARCHITECT.onSurfaceVariant }}>
            {loading
              ? "Cargando métricas del ecosistema..."
              : `El sistema opera al ${Math.round(capacity)}% de su capacidad${latency !== null ? ` · latencia ${latency.toFixed(1)}ms` : ""}. ${totalLeads} leads totales, ${convRate}% de conversión.`}
          </p>

          {/* Key metrics row */}
          {!loading && metrics && (
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: "Margen", value: fmtUSD(marginUsd), color: marginUsd >= 0 ? "#22c55e" : "#ff7351" },
                { label: "Ganados", value: wonLeads, color: ARCHITECT.primary },
                { label: "Dominios", value: customDomains, color: ARCHITECT.onSurface },
              ].map(m => (
                <div
                  key={m.label}
                  className="rounded-xl p-3"
                  style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
                >
                  <p className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: ARCHITECT.onSurfaceVariant }}>
                    {m.label}
                  </p>
                  <p
                    className="text-xl font-black mt-1"
                    style={{ color: m.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {m.value}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* 7-day sparkline */}
          {!loading && dailyIncome.length > 0 && (
            <div
              data-testid="sparkline-card"
              className="rounded-xl p-4 mb-6"
              style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: ARCHITECT.onSurfaceVariant }}>
                    Últimos 7 días · USD
                  </p>
                  <p
                    className="text-lg font-black mt-0.5"
                    style={{ color: ARCHITECT.primary, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {fmtUSD(incomeWeekTotal)}
                  </p>
                </div>
                <div className="flex items-center gap-3 text-[10px]" style={{ color: ARCHITECT.onSurfaceVariant }}>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-0.5" style={{ background: ARCHITECT.primary }} />
                    Ingresos
                  </span>
                  <span className="flex items-center gap-1">
                    <span
                      className="w-2 h-0.5"
                      style={{ background: "#ff7351", backgroundImage: "repeating-linear-gradient(90deg, #ff7351 0 2px, transparent 2px 4px)" }}
                    />
                    Egresos
                  </span>
                </div>
              </div>
              <Sparkline income={dailyIncome} expense={dailyExpense} primary={ARCHITECT.primary} />
            </div>
          )}

          <button
            data-testid="save-settings-btn"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-black transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
            style={{
              background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`,
              color: "#0e0e0e",
              boxShadow: `0 16px 40px -12px ${ARCHITECT.primary}55`,
            }}
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? "Guardando..." : "Guardar configuración"}
          </button>
        </div>

        {/* Right side: Latency gauge + Finance breakdown */}
        <div className="flex justify-center md:justify-end">
          <div
            className="p-8 rounded-2xl w-full max-w-sm"
            style={{
              background: ARCHITECT.surfaceContainer,
              border: `1px solid ${ARCHITECT.ghost}`,
              boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <span className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: ARCHITECT.onSurfaceVariant }}>
                Latencia Mongo
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: latencyColor }}>
                {latencyStatus}
              </span>
            </div>
            <div
              data-testid="latency-value"
              className="text-6xl font-black mb-3"
              style={{ color: latencyColor, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              {loading ? "…" : latency === null ? "—" : latency.toFixed(1)}
              <span className="text-xl ml-1 font-normal" style={{ color: `${ARCHITECT.onSurfaceVariant}80` }}>
                ms
              </span>
            </div>
            <div className="w-full h-1 rounded-full overflow-hidden mb-6" style={{ background: ARCHITECT.surfaceLow }}>
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${capacity}%`,
                  background: latencyColor,
                  boxShadow: `0 0 10px ${latencyColor}80`,
                }}
              />
            </div>

            {/* Finance mini-breakdown */}
            {!loading && metrics?.finance?.by_currency?.length > 0 && (
              <div className="space-y-2 pt-5" style={{ borderTop: `1px solid ${ARCHITECT.ghost}` }}>
                <p className="text-[9px] font-bold uppercase tracking-[0.25em]" style={{ color: ARCHITECT.onSurfaceVariant }}>
                  Margen por moneda
                </p>
                {metrics.finance.by_currency.slice(0, 3).map(c => {
                  const positive = c.margin >= 0;
                  return (
                    <div key={c.currency} className="flex items-center justify-between text-xs">
                      <span className="font-semibold" style={{ color: ARCHITECT.onSurface }}>
                        {c.currency}
                      </span>
                      <span
                        className="font-mono font-black"
                        style={{ color: positive ? "#22c55e" : "#ff7351" }}
                      >
                        {positive ? "+" : ""}{c.margin.toLocaleString("es-UY")}
                      </span>
                    </div>
                  );
                })}
                <div
                  className="flex items-center justify-between pt-2 mt-2 text-[11px]"
                  style={{ borderTop: `1px solid ${ARCHITECT.ghost}` }}
                >
                  <span className="font-bold" style={{ color: ARCHITECT.onSurfaceVariant }}>
                    Pagos últimos 30 días
                  </span>
                  <span className="font-mono font-black" style={{ color: ARCHITECT.primary }}>
                    {recentPayments}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Domain Section ────────────────────────────────────────────────────────────

const STEPS = [
  {
    num: "1",
    title: "Ir al panel de Emergent",
    desc: 'En app.emergent.sh, abre tu proyecto y hacé clic en el botón "Deploy" o "Settings".',
    link: "https://app.emergent.sh",
    linkLabel: "Abrir Emergent",
  },
  {
    num: "2",
    title: 'Clic en "Link domain"',
    desc: 'Encontrarás la opción "Link domain" en la sección de deployment de tu proyecto.',
  },
  {
    num: "3",
    title: "Ingresá tu dominio",
    desc: 'Escribí tu dominio (ej: sitiosya.com.uy) y hacé clic en "Entri".',
  },
  {
    num: "4",
    title: "Seguí el flujo de Entri",
    desc: "Entri detecta tu proveedor automáticamente y configura los registros DNS por vos.",
  },
  {
    num: "5",
    title: "Esperá la propagación",
    desc: "Los cambios DNS tardan entre 5–15 minutos (hasta 24h en casos extremos).",
  },
];


// ── AI & System Section (model overrides + maintenance) ─────────────────────
function OpenRouterKeyCard() {
  // Iter91.5 — Admin can paste their OpenRouter key here without redeploying.
  // Validates against /credits, persists to db.settings.openrouter_api_key,
  // and applies at runtime so subsequent AI calls pick it up.
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draftKey, setDraftKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = async (showToast = false) => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/settings/openrouter`);
      if (!r) return; // 401 → redirect already handled
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        const msg = err.detail || `HTTP ${r.status}`;
        setLoadError(msg);
        if (showToast) toast.error(`OpenRouter: ${msg}`);
        return;
      }
      setLoadError("");
      setInfo(await r.json());
    } catch (e) {
      const msg = e?.message || "Sin conexión";
      setLoadError(msg);
      // Iter91.7 — only surface a toast when the user explicitly refreshed.
      // Auto-refresh failures stay inline so the admin doesn't get spammed.
      if (showToast) toast.error(`Error cargando OpenRouter: ${msg}`);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load(false);
    // Iter91.6 — live refresh del saldo cada 30s para que el admin vea
    // el consumo en tiempo casi-real sin recargar.
    const id = setInterval(() => load(false), 30000);
    return () => clearInterval(id);
  /* eslint-disable-next-line */ }, []);

  const handleSave = async () => {
    const trimmed = draftKey.trim();
    if (!trimmed.startsWith("sk-or-")) {
      toast.error("La clave debe empezar con 'sk-or-' (sin espacios al inicio)");
      return;
    }
    setSaving(true);
    try {
      const r = await authFetch(`${API}/settings/openrouter`, {
        method: "PUT", body: JSON.stringify({ api_key: trimmed }),
      });
      if (!r) return;
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        // Surface backend `detail` verbatim so the admin sees WHY OpenRouter
        // rejected the key (common cases: HTTP 401 = wrong key, HTTP 402 = no
        // saldo, HTTP 503 = network down).
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      toast.success("Clave OpenRouter actualizada");
      setEditing(false); setDraftKey("");
      await load(true);
    } catch (e) { toast.error(e.message || "OpenRouter rechazó la clave"); }
    finally { setSaving(false); }
  };

  const balance = info?.balance;
  const lowBalance = balance && balance.pct_remaining < 15;
  return (
    <BentoCard className="col-span-12 p-6 md:p-8" data-testid="openrouter-key-card">
      <div className="flex items-start gap-3 mb-5">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(168, 85, 247, 0.1)", border: "1px solid rgba(168, 85, 247, 0.25)" }}
        >
          <Sparkles size={16} style={{ color: "#A855F7" }} />
        </div>
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Clave OpenRouter
          </h3>
          <p className="text-xs mt-1" style={{ color: ARCHITECT.onSurfaceVariant }}>
            Esta clave alimenta TODA la generación de IA: sitios web, edición del cliente, agente, scripts.
            Si el saldo se agota, los modelos premium fallan y el sistema cae a alternativas peores.
            Conseguila en <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer"
              className="underline decoration-dotted" style={{ color: "#A855F7" }}>openrouter.ai/keys</a>.
          </p>
        </div>
      </div>

      {loading && !info ? (
        <div className="flex justify-center py-6">
          <Loader2 className="animate-spin" size={18} style={{ color: "#A855F7" }} />
        </div>
      ) : (loadError && !info) ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg"
               style={{ background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.25)" }}
               data-testid="openrouter-load-error">
            <AlertCircle size={14} className="text-red-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-bold text-red-300">No pude leer el estado de OpenRouter</p>
              <p className="text-[11px] text-red-200/80 mt-1 font-mono break-all">{loadError}</p>
            </div>
          </div>
          <button onClick={() => load(true)}
                  data-testid="openrouter-retry-btn"
                  className="text-[11px] font-bold text-purple-300 hover:text-purple-200 underline decoration-dotted">
            Reintentar
          </button>
        </div>
      ) : !info?.configured ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
               style={{ background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.25)" }}>
            <AlertCircle size={14} className="text-red-400" />
            <span className="text-xs font-bold text-red-300">No hay clave configurada — la IA no funcionará</span>
          </div>
          <input
            type="password"
            value={draftKey}
            onChange={e => setDraftKey(e.target.value)}
            placeholder="sk-or-v1-..."
            data-testid="openrouter-key-input"
            className="w-full px-3 py-2.5 rounded-lg text-sm font-mono bg-[#1a1a1a] border border-[#2a2a2a] focus:border-purple-500 outline-none text-white"
          />
          <button
            onClick={handleSave}
            disabled={saving || !draftKey}
            data-testid="openrouter-key-save-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)", color: "white" }}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Validar y guardar
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Saldo */}
          <div className="md:col-span-2 p-4 rounded-xl"
               style={{ background: lowBalance ? "rgba(239, 68, 68, 0.06)" : "rgba(168, 85, 247, 0.05)",
                         border: lowBalance ? "1px solid rgba(239, 68, 68, 0.25)" : "1px solid rgba(168, 85, 247, 0.18)" }}>
            <p className="text-[10px] font-black uppercase tracking-widest mb-2"
               style={{ color: lowBalance ? "#FCA5A5" : "#C4B5FD" }}>
              Saldo OpenRouter {lowBalance && "· BAJO"}
            </p>
            {balance ? (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className={`text-3xl font-black ${lowBalance ? "text-red-300" : "text-white"}`}
                        data-testid="openrouter-balance-remaining">
                    ${balance.remaining_usd.toFixed(2)}
                  </span>
                  <span className="text-xs" style={{ color: ARCHITECT.onSurfaceVariant }}>
                    de ${balance.limit_usd.toFixed(2)} ({balance.pct_remaining}%)
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden bg-white/[0.06]">
                  <div className={`h-full transition-all ${lowBalance ? "bg-red-500" : "bg-purple-500"}`}
                       style={{ width: `${Math.max(0, Math.min(100, balance.pct_remaining))}%` }} />
                </div>
                {lowBalance && (
                  <a href="https://openrouter.ai/credits" target="_blank" rel="noreferrer"
                     data-testid="openrouter-recharge-link"
                     className="inline-flex items-center gap-1 mt-3 text-[11px] font-bold text-red-300 hover:text-red-200">
                    Recargar ahora →
                  </a>
                )}
              </>
            ) : (
              <span className="text-xs text-amber-300">{info.error || "No pude leer el saldo"}</span>
            )}
          </div>

          {/* Clave + acciones */}
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10">
            <p className="text-[10px] font-black uppercase tracking-widest mb-2"
               style={{ color: ARCHITECT.onSurfaceVariant }}>
              Clave activa
            </p>
            <p className="text-xs font-mono text-white mb-3" data-testid="openrouter-key-masked">
              {info.masked}
            </p>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                data-testid="openrouter-key-edit-btn"
                className="text-[11px] font-bold text-purple-300 hover:text-purple-200 underline decoration-dotted"
              >
                Cambiar clave
              </button>
            ) : (
              <div className="space-y-2">
                <input
                  type="password"
                  value={draftKey}
                  onChange={e => setDraftKey(e.target.value)}
                  placeholder="sk-or-v1-..."
                  className="w-full px-2 py-1.5 rounded-md text-[11px] font-mono bg-[#1a1a1a] border border-[#2a2a2a] focus:border-purple-500 outline-none text-white"
                />
                <div className="flex gap-1.5">
                  <button onClick={handleSave} disabled={saving || !draftKey}
                          data-testid="openrouter-key-save-btn"
                          className="flex-1 text-[10px] font-bold py-1.5 rounded bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-white">
                    {saving ? "Validando…" : "Guardar"}
                  </button>
                  <button onClick={() => { setEditing(false); setDraftKey(""); }}
                          className="flex-1 text-[10px] font-bold py-1.5 rounded bg-white/[0.06] hover:bg-white/[0.12] text-gray-300">
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </BentoCard>
  );
}


function AIAndSystemSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [active, setActive] = useState({ site_code: "", site_design: "", web_script: "", research_primary: "", site_edit: "", client_edit_html: "", client_edit_visual: "", client_edit_text: "" });
  const [defaults, setDefaults] = useState({ site_code: "", site_design: "", web_script: "", research_primary: "", site_edit: "", client_edit_html: "", client_edit_visual: "", client_edit_text: "" });
  const [stored, setStored] = useState({ site_code: "", site_design: "", web_script: "", research_primary: "", site_edit: "", client_edit_html: "", client_edit_visual: "", client_edit_text: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${API}/settings/ai-models`);
        if (!r?.ok) throw new Error();
        const d = await r.json();
        if (!alive) return;
        setActive(d.active || {});
        setDefaults(d.defaults || {});
        setStored(d.stored || { site_code: "", site_design: "", web_script: "", research_primary: "", site_edit: "", client_edit_html: "", client_edit_visual: "", client_edit_text: "" });
      } catch (e) {
        console.error("[Settings/ai] load failed:", e);
        toast.error("Error cargando modelos IA");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const handleField = (k, v) => setStored(s => ({ ...s, [k]: v }));

  const handleSaveModels = async () => {
    setSaving(true);
    try {
      const r = await authFetch(`${API}/settings/ai-models`, {
        method: "PUT",
        body: JSON.stringify(stored),
      });
      if (!r?.ok) throw new Error();
      const d = await r.json();
      setActive(d.active || {});
      toast.success("Modelos IA actualizados");
    } catch {
      toast.error("Error al guardar");
    } finally { setSaving(false); }
  };

  const handleResetField = (k) => setStored(s => ({ ...s, [k]: "" }));

  const handleCleanup = async () => {
    if (!window.confirm(
      "¿Eliminar TODOS los datos de prueba? Esto borra:\n" +
      " • Leads con emails @test/@ejemplo/@example/etc.\n" +
      " • Threads de bandeja de rebotes (mailer-daemon)\n" +
      " • Cuentas de clientes con emails nulos/test\n" +
      " • Transacciones Stripe con session_id de test\n\n" +
      "Esta acción es irreversible."
    )) return;
    setCleaning(true);
    try {
      const r = await authFetch(`${API}/admin/cleanup-test-data`, { method: "POST" });
      if (!r?.ok) throw new Error();
      const d = await r.json();
      const x = d.removed || {};
      toast.success(
        `Limpieza OK — Leads: ${x.businesses || 0} · Threads bounce: ${x.bounce_threads || 0} · Cuentas: ${x.client_accounts || 0} · Stripe test: ${x.test_payments || 0}`,
        { duration: 8000 }
      );
    } catch {
      toast.error("Error ejecutando limpieza");
    } finally { setCleaning(false); }
  };

  const platformFields = [
    { key: "site_code",        label: "Creación de HTML",           icon: <Code2 size={14} />,         desc: "Modelo que genera el código HTML final del sitio.",            placeholder: "anthropic/claude-sonnet-4.6" },
    { key: "site_design",      label: "Interfaz gráfica",           icon: <Palette size={14} />,       desc: "Modelo que diseña la UI y el spec visual.",                    placeholder: "google/gemini-3-flash-preview" },
    { key: "web_script",       label: "Script persuasivo web",      icon: <Wand2 size={14} />,         desc: "Modelo para copy persuasivo y outreach.",                      placeholder: "anthropic/claude-opus-4.7" },
    { key: "research_primary", label: "Investigación de empresas",  icon: <FlaskConical size={14} />,  desc: "Modelo que investiga el negocio (datos, contexto, perfil).",   placeholder: "google/gemini-2.5-flash-lite" },
  ];
  const clientFields = [
    { key: "client_edit_html",   label: "Editar HTML / Estructura",  icon: <Code2 size={14} />,         desc: "Modelo para cambios estructurales o de código en el sitio del cliente.",         placeholder: "anthropic/claude-sonnet-4.6" },
    { key: "client_edit_visual", label: "Editar Interfaz / CSS",     icon: <Palette size={14} />,       desc: "Modelo para cambios visuales: colores, espaciados, tipografías.",                placeholder: "google/gemini-3-flash-preview" },
    { key: "client_edit_text",   label: "Edición rápida de texto",   icon: <MessageSquare size={14} />, desc: "Modelo rápido y barato para cambios de copy o texto simple.",                    placeholder: "moonshotai/kimi-k2.6" },
    { key: "site_edit",          label: "Chat del editor",           icon: <MessageSquare size={14} />, desc: "Modelo para la conversación del chat lateral del cliente (no edita el sitio). Recomendado: un modelo gratuito y rápido como Gemini 2.5 Flash Lite.", placeholder: "google/gemini-2.5-flash-lite" },
  ];

  const renderField = (f) => {
    const currentActive = active[f.key] || "";
    const usingOverride = (stored[f.key] || "").trim().length > 0;
    return (
      <div
        key={f.key}
        data-testid={`ai-model-card-${f.key}`}
        className="p-4 rounded-xl"
        style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
      >
        <div className="flex items-center gap-2 mb-2">
          <span style={{ color: ARCHITECT.primary }}>{f.icon}</span>
          <p className="text-sm font-bold">{f.label}</p>
        </div>
        <p className="text-[11px] mb-3" style={{ color: ARCHITECT.onSurfaceVariant }}>{f.desc}</p>
        <input
          data-testid={`ai-model-input-${f.key}`}
          type="text"
          value={stored[f.key] || ""}
          onChange={(e) => handleField(f.key, e.target.value)}
          placeholder={f.placeholder}
          className="w-full px-3 py-2 text-xs font-mono rounded-lg focus:outline-none focus:ring-1"
          style={{
            background: ARCHITECT.surfaceHigh,
            border: `1px solid ${ARCHITECT.ghost}`,
            color: ARCHITECT.onSurface,
            "--tw-ring-color": ARCHITECT.primary,
          }}
        />
        <div className="mt-2 flex items-center justify-between gap-2 text-[10px]">
          <span style={{ color: ARCHITECT.onSurfaceVariant }}>
            Activo:&nbsp;
            <span className="font-mono" style={{ color: usingOverride ? ARCHITECT.primary : ARCHITECT.onSurfaceVariant }}>
              {currentActive || defaults[f.key]}
            </span>
          </span>
          {(stored[f.key] || "").trim() && (
            <button
              data-testid={`ai-model-reset-${f.key}`}
              onClick={() => handleResetField(f.key)}
              className="underline decoration-dotted"
              style={{ color: ARCHITECT.onSurfaceVariant }}
            >
              usar default
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <Loader2 className="animate-spin" size={24} style={{ color: ARCHITECT.primary }} />
    </div>
  );

  return (
    <div className="grid grid-cols-12 gap-4 md:gap-6 pb-12">
      {/* ── OpenRouter API Key card (Iter91.5) ── */}
      <OpenRouterKeyCard />

      {/* ── AI Models Card ─── */}
      <BentoCard className="col-span-12 p-6 md:p-8">
        <div className="flex items-start gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${ARCHITECT.primary}12`, border: `1px solid ${ARCHITECT.primary}25` }}
          >
            <Brain size={16} style={{ color: ARCHITECT.primary }} />
          </div>
          <div>
            <h3 className="text-xl md:text-2xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Modelos de IA
            </h3>
            <p className="text-xs mt-1" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Elige qué modelo de OpenRouter usa cada tarea. Pegá el ID tal como aparece en{" "}
              <a href="https://openrouter.ai/models" target="_blank" rel="noreferrer"
                 className="underline decoration-dotted" style={{ color: ARCHITECT.primary }}>
                openrouter.ai/models
              </a>. Si dejás un campo vacío, se usa el default.
            </p>
          </div>
        </div>

        {/* ── SitiosYA section (platform-side AI) ── */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-4 rounded-sm" style={{ background: ARCHITECT.primary }} />
          <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: ARCHITECT.primary }}>
            SitiosYA · Plataforma
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {platformFields.map(renderField)}
        </div>

        {/* ── Cliente section (client editor AI) ── */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1 h-4 rounded-sm" style={{ background: "#34D399" }} />
          <p className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: "#34D399" }}>
            Cliente · Editor
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {clientFields.map(renderField)}
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            data-testid="ai-models-save-btn"
            onClick={handleSaveModels}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-bold rounded-xl transition-all disabled:opacity-50"
            style={{
              background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`,
              color: "#0e0e0e",
              boxShadow: `0 8px 24px -8px ${ARCHITECT.primary}55`,
            }}
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? "Guardando..." : "Guardar modelos"}
          </button>
        </div>
      </BentoCard>

      {/* ── System Maintenance Card ─── */}
      <BentoCard className="col-span-12 p-6 md:p-8">
        <div className="flex items-start gap-3 mb-6">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.20)" }}
          >
            <AlertCircle size={16} style={{ color: "#f87171" }} />
          </div>
          <div>
            <h3 className="text-xl md:text-2xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Mantenimiento
            </h3>
            <p className="text-xs mt-1" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Ejecuta limpiezas seguras en producción. Borra datos con patrones explícitos de prueba; no toca datos reales.
            </p>
          </div>
        </div>

        <div
          className="p-4 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4"
          style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold mb-1">Limpiar datos de prueba</p>
            <p className="text-[11px]" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Borra leads con emails <span className="font-mono text-white">@test.com</span>,{" "}
              <span className="font-mono text-white">@example.com</span>,{" "}
              <span className="font-mono text-white">@ejemplo.com</span>, threads de rebote{" "}
              <span className="font-mono text-white">mailer-daemon</span>, cuentas de cliente vacías y sesiones Stripe{" "}
              <span className="font-mono text-white">cs_test_*</span>.
            </p>
          </div>
          <button
            data-testid="cleanup-test-data-btn"
            onClick={handleCleanup}
            disabled={cleaning}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-bold rounded-xl transition-colors disabled:opacity-50 flex-shrink-0"
            style={{ background: "rgba(239,68,68,0.10)", color: "#f87171", border: "1px solid rgba(239,68,68,0.25)" }}
          >
            {cleaning ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            {cleaning ? "Limpiando..." : "Ejecutar limpieza"}
          </button>
        </div>
      </BentoCard>
    </div>
  );
}


function DomainSection() {
  const appUrl = process.env.REACT_APP_BACKEND_URL || "";
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiado"); };

  return (
    <div className="grid grid-cols-12 gap-5 md:gap-6 pb-8">
      {/* Header banner */}
      <BentoCard className="col-span-12 p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${ARCHITECT.primary}12` }}
          >
            <Globe size={20} style={{ color: ARCHITECT.primary }} />
          </div>
          <div className="flex-1">
            <SectionKicker>Dominio de plataforma</SectionKicker>
            <h3
              className="text-2xl md:text-3xl font-black mb-2 tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Conectá tu dominio propio
            </h3>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Vinculá <strong style={{ color: ARCHITECT.onSurface }}>sitiosya.com.uy</strong> para que toda la plataforma sea accesible desde tu dirección en lugar de la URL de preview de Emergent.
            </p>
          </div>
        </div>
      </BentoCard>

      {/* Current URL */}
      <BentoCard className="col-span-12 lg:col-span-5 p-6 md:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${ARCHITECT.primary}10` }}>
            <Activity size={16} style={{ color: ARCHITECT.primary }} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: ARCHITECT.onSurfaceVariant }}>Estado actual</p>
            <h4 className="text-base font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>URL de la plataforma</h4>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-xl p-3" style={{ background: ARCHITECT.surfaceLow }}>
          <code className="text-xs flex-1 break-all" style={{ color: ARCHITECT.primary }}>{appUrl}</code>
          <button
            onClick={() => copy(appUrl)}
            className="flex-shrink-0 p-2 rounded-lg"
            style={{ color: ARCHITECT.onSurfaceVariant, background: ARCHITECT.surfaceHigh }}
          >
            <Copy size={13} />
          </button>
        </div>
      </BentoCard>

      {/* Important note */}
      <BentoCard className="col-span-12 lg:col-span-7 p-6 md:p-8" style={{ borderColor: "rgba(255,115,81,0.25)" }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,115,81,0.12)" }}>
            <AlertCircle size={16} style={{ color: "#ff7351" }} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: "#ff7351" }}>Antes de empezar</p>
            <h4 className="text-base font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Limpia tus registros DNS</h4>
            <p className="text-sm leading-relaxed" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Eliminá todos los <strong style={{ color: ARCHITECT.onSurface }}>registros A</strong> existentes en tu proveedor de dominio antes de vincular. Esto evita conflictos durante la configuración.
            </p>
          </div>
        </div>
      </BentoCard>

      {/* Steps */}
      <BentoCard className="col-span-12 p-6 md:p-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${ARCHITECT.primary}10` }}>
            <KeyRound size={16} style={{ color: ARCHITECT.primary }} />
          </div>
          <div>
            <SectionKicker>Protocolo</SectionKicker>
            <h3 className="text-2xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Cómo vincular tu dominio</h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STEPS.map((step) => (
            <div
              key={step.num}
              className="p-5 rounded-xl transition-all"
              style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-black"
                  style={{ background: `${ARCHITECT.primary}15`, color: ARCHITECT.primary }}
                >
                  {step.num}
                </div>
                <p className="text-sm font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{step.title}</p>
              </div>
              <p className="text-xs leading-relaxed mb-3" style={{ color: ARCHITECT.onSurfaceVariant }}>{step.desc}</p>
              {step.link && (
                <a
                  href={step.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="emergent-link"
                  className="inline-flex items-center gap-1 text-xs font-bold hover:underline"
                  style={{ color: ARCHITECT.primary }}
                >
                  {step.linkLabel} <ExternalLink size={11} />
                </a>
              )}
            </div>
          ))}
        </div>
      </BentoCard>

      {/* Confirmation */}
      <BentoCard className="col-span-12 p-6 md:p-8" style={{ borderColor: "rgba(34,197,94,0.2)" }}>
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(34,197,94,0.1)" }}>
            <CheckCircle2 size={16} style={{ color: "#22c55e" }} />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: "#22c55e" }}>Soportados</p>
            <h4 className="text-base font-bold mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Dominios raíz y subdominios</h4>
            <p className="text-sm leading-relaxed" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Tanto el dominio raíz (<code className="font-mono" style={{ color: ARCHITECT.primary }}>sitiosya.com.uy</code>) como el subdominio (<code className="font-mono" style={{ color: ARCHITECT.primary }}>www.sitiosya.com.uy</code>) son soportados por Emergent vía el flujo Entri.
            </p>
          </div>
        </div>
      </BentoCard>
    </div>
  );
}

// ── Users Section ─────────────────────────────────────────────────────────────

const PRESENCE_CONFIG = {
  online:  { color: "#22c55e", label: "En línea" },
  away:    { color: "#eab308", label: "Ausente" },
  offline: { color: "#6b7280", label: "Desconectado" },
};

function UsersSection() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/users`);
      const data = await res?.json();
      if (Array.isArray(data)) setUsers(data);
    } catch (err) { console.error("[Settings] Users load failed:", err); toast.error("Error al cargar usuarios"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await authFetch(`${API}/users/invite`, {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail.trim().toLowerCase(), name: inviteName.trim() }),
      });
      const d = await res?.json();
      if (!res?.ok) { toast.error(d?.detail || "Error al invitar"); return; }
      toast.success(`Invitación enviada a ${inviteEmail}`);
      if (d?.register_url) {
        navigator.clipboard.writeText(d.register_url).catch(() => {});
        toast.info("Link de registro copiado al portapapeles");
      }
      setInviteEmail("");
      setInviteName("");
      load();
    } catch { toast.error("Error al invitar"); }
    finally { setInviting(false); }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`¿Eliminar al usuario ${email}?`)) return;
    setDeletingId(id);
    try {
      const res = await authFetch(`${API}/users/${id}`, { method: "DELETE" });
      if (res?.ok) { toast.success("Usuario eliminado"); setUsers(u => u.filter(x => x.id !== id)); }
      else toast.error("Error al eliminar");
    } catch { toast.error("Error al eliminar"); }
    finally { setDeletingId(null); }
  };

  const initials = (name) =>
    (name || "?").split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase();

  if (loading) return (
    <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: ARCHITECT.primary }} /></div>
  );

  return (
    <div className="grid grid-cols-12 gap-5 md:gap-6 pb-8">
      {/* Invite form */}
      <BentoCard className="col-span-12 lg:col-span-5 p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${ARCHITECT.primary}12` }}>
            <UserPlus size={16} style={{ color: ARCHITECT.primary }} />
          </div>
          <div>
            <SectionKicker>Equipo</SectionKicker>
            <h3 className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Invitar nuevo usuario
            </h3>
          </div>
        </div>
        <form onSubmit={invite} className="flex flex-col gap-3">
          <input
            data-testid="invite-name-input"
            type="text"
            value={inviteName}
            onChange={e => setInviteName(e.target.value)}
            placeholder="Nombre (opcional)"
            className="w-full rounded-xl px-4 py-3 text-sm transition-all focus:outline-none"
            style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}`, color: ARCHITECT.onSurface }}
          />
          <input
            data-testid="invite-email-input"
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            placeholder="correo@empresa.com"
            required
            className="w-full rounded-xl px-4 py-3 text-sm transition-all focus:outline-none"
            style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}`, color: ARCHITECT.onSurface }}
          />
          <button
            data-testid="invite-submit-btn"
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
            className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-black rounded-xl disabled:opacity-50 transition-all hover:brightness-110"
            style={{
              background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`,
              color: "#0e0e0e",
            }}
          >
            {inviting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Invitar y enviar email
          </button>
          <p className="text-[11px] leading-relaxed" style={{ color: ARCHITECT.onSurfaceVariant }}>
            Se enviará un email de invitación con un link para crear la cuenta. El link también se copia al portapapeles.
          </p>
        </form>
      </BentoCard>

      {/* User list */}
      <BentoCard className="col-span-12 lg:col-span-7 p-0 overflow-hidden">
        <div className="flex items-center justify-between px-6 md:px-8 py-5" style={{ borderBottom: `1px solid ${ARCHITECT.ghost}` }}>
          <div>
            <SectionKicker>Arquitectos</SectionKicker>
            <h3 className="text-xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Equipo <span style={{ color: ARCHITECT.onSurfaceVariant }}>({users.length})</span>
            </h3>
          </div>
          <button
            onClick={load}
            className="p-2 rounded-lg transition-colors"
            style={{ color: ARCHITECT.onSurfaceVariant, background: ARCHITECT.surfaceLow }}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        {users.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Users size={32} className="mx-auto mb-3 opacity-30" style={{ color: ARCHITECT.onSurfaceVariant }} />
            <p className="text-sm" style={{ color: ARCHITECT.onSurfaceVariant }}>No hay usuarios invitados aún</p>
          </div>
        ) : (
          <div>
            {users.map(u => {
              const pres = PRESENCE_CONFIG[u.presence || "offline"];
              return (
                <div
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="flex items-center gap-4 px-6 md:px-8 py-4 transition-colors"
                  style={{ borderTop: `1px solid ${ARCHITECT.ghost}` }}
                >
                  <div className="relative flex-shrink-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: `${ARCHITECT.primary}20` }}
                    >
                      <span className="text-xs font-black" style={{ color: ARCHITECT.primary }}>{initials(u.name)}</span>
                    </div>
                    <span
                      data-testid={`presence-${u.id}`}
                      className="absolute bottom-0 right-0 w-3 h-3 rounded-full"
                      style={{ background: pres.color, border: `2px solid ${ARCHITECT.surfaceContainer}` }}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold truncate" style={{ color: ARCHITECT.onSurface }}>{u.name}</p>
                      {u.role === "admin" && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                          style={{ color: ARCHITECT.primary, background: `${ARCHITECT.primary}15` }}>
                          Admin
                        </span>
                      )}
                      {u.status === "pending" && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider"
                          style={{ color: "#ff7351", background: "rgba(255,115,81,0.12)" }}>
                          Pendiente
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs truncate" style={{ color: ARCHITECT.onSurfaceVariant }}>{u.email}</p>
                      <span className="text-[10px]" style={{ color: `${ARCHITECT.onSurfaceVariant}60` }}>·</span>
                      <span className="text-[10px] font-semibold" style={{ color: pres.color }}>{pres.label}</span>
                    </div>
                  </div>

                  <button
                    data-testid={`delete-user-${u.id}`}
                    onClick={() => deleteUser(u.id, u.email)}
                    disabled={deletingId === u.id}
                    className="p-2 rounded-lg transition-colors disabled:opacity-50 hover:bg-red-500/10"
                    style={{ color: ARCHITECT.onSurfaceVariant }}
                  >
                    {deletingId === u.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </BentoCard>

      {/* Presence legend */}
      <BentoCard className="col-span-12 p-6">
        <div className="flex items-center gap-6 flex-wrap">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: ARCHITECT.primary }}>
            Estado de presencia
          </p>
          {Object.entries(PRESENCE_CONFIG).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: v.color }} />
              <span className="text-[11px]" style={{ color: ARCHITECT.onSurfaceVariant }}>{v.label}</span>
            </div>
          ))}
          <span className="text-[11px] ml-auto" style={{ color: `${ARCHITECT.onSurfaceVariant}80` }}>
            En línea: 5 min · Ausente: 5-30 min · Desconectado: &gt;30 min
          </span>
        </div>
      </BentoCard>
    </div>
  );
}

// ── Contact Fields Section ────────────────────────────────────────────────────

function ContactFieldsSection() {
  const [systemFields, setSystemFields] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [form, setForm] = useState({ label: "", field_id: "", description: "", type: "text", visibility: "hide_empty" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/settings/contact-fields`);
      const data = await res?.json();
      if (data) {
        setSystemFields(data.system || []);
        setCustomFields(data.custom || []);
      }
    } catch (err) { console.error("[Settings] Fields load failed:", err); toast.error("Error al cargar campos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditingField(null);
    setForm({ label: "", field_id: "", description: "", type: "text", visibility: "hide_empty" });
    setShowForm(true);
  };

  const openEdit = (field) => {
    setEditingField(field);
    setForm({ label: field.label, field_id: field.field_id, description: field.description || "", type: field.type, visibility: field.visibility });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.label.trim() || !form.field_id.trim()) {
      toast.error("Nombre e ID son requeridos");
      return;
    }
    setSaving(true);
    try {
      let res;
      if (editingField) {
        res = await authFetch(`${API}/settings/contact-fields/${editingField.id}`, { method: "PUT", body: JSON.stringify(form) });
      } else {
        res = await authFetch(`${API}/settings/contact-fields`, { method: "POST", body: JSON.stringify(form) });
      }
      if (!res?.ok) {
        const err = await res?.json();
        throw new Error(err?.detail || "Error");
      }
      toast.success(editingField ? "Campo actualizado" : "Campo creado");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.message || "Error al guardar campo");
    } finally { setSaving(false); }
  };

  const handleDelete = async (field) => {
    if (!window.confirm(`¿Eliminar campo "${field.label}"?`)) return;
    try {
      const res = await authFetch(`${API}/settings/contact-fields/${field.id}`, { method: "DELETE" });
      if (!res?.ok) throw new Error();
      toast.success("Campo eliminado");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const autoFieldId = (label) => {
    return label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
  };

  if (loading) return (
    <div className="flex justify-center py-16"><Loader2 className="animate-spin" style={{ color: ARCHITECT.primary }} /></div>
  );

  return (
    <div className="grid grid-cols-12 gap-5 md:gap-6 pb-8">
      {/* Header */}
      <BentoCard className="col-span-12 p-6 md:p-8 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${ARCHITECT.primary}12` }}>
            <Database size={20} style={{ color: ARCHITECT.primary }} />
          </div>
          <div>
            <SectionKicker>Modelo de datos</SectionKicker>
            <h3 className="text-2xl md:text-3xl font-black tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Campos de negocio
            </h3>
            <p className="text-sm mt-1" style={{ color: ARCHITECT.onSurfaceVariant }}>
              Campos del sistema + tus campos personalizados
            </p>
          </div>
        </div>
        <button
          data-testid="add-field-btn"
          onClick={openAdd}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-black transition-all hover:brightness-110"
          style={{
            background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`,
            color: "#0e0e0e",
          }}
        >
          <Plus size={14} /> Nuevo campo
        </button>
      </BentoCard>

      {/* Modal form */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
          <div
            className="rounded-2xl p-6 w-full max-w-md"
            style={{
              background: ARCHITECT.surfaceContainer,
              border: `1px solid ${ARCHITECT.ghost}`,
              boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
            }}
          >
            <SectionKicker>{editingField ? "Edición" : "Nuevo"}</SectionKicker>
            <h3 className="text-xl font-black mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {editingField ? "Editar campo" : "Nuevo campo personalizado"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 block" style={{ color: ARCHITECT.onSurfaceVariant }}>Nombre del campo *</label>
                <input
                  data-testid="field-label-input"
                  value={form.label}
                  onChange={e => {
                    const label = e.target.value;
                    setForm(f => ({ ...f, label, field_id: !editingField ? autoFieldId(label) : f.field_id }));
                  }}
                  placeholder="ej: Opción del menú"
                  className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none"
                  style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}`, color: ARCHITECT.onSurface }}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 block" style={{ color: ARCHITECT.onSurfaceVariant }}>ID del campo *</label>
                <input
                  data-testid="field-id-input"
                  value={form.field_id}
                  onChange={e => setForm(f => ({ ...f, field_id: e.target.value }))}
                  disabled={!!editingField}
                  placeholder="ej: opcion_menu"
                  className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none disabled:opacity-60 font-mono"
                  style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}`, color: ARCHITECT.onSurface }}
                />
                <p className="text-[10px] mt-1.5" style={{ color: ARCHITECT.onSurfaceVariant }}>
                  Se usa como clave en la base de datos. Solo letras, números y _
                </p>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 block" style={{ color: ARCHITECT.onSurfaceVariant }}>Descripción</label>
                <input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Descripción del campo (opcional)"
                  className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none"
                  style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}`, color: ARCHITECT.onSurface }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 block" style={{ color: ARCHITECT.onSurfaceVariant }}>Tipo</label>
                  <select
                    value={form.type}
                    onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none"
                    style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}`, color: ARCHITECT.onSurface }}
                  >
                    {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 block" style={{ color: ARCHITECT.onSurfaceVariant }}>Visibilidad</label>
                  <select
                    value={form.visibility}
                    onChange={e => setForm(f => ({ ...f, visibility: e.target.value }))}
                    className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none"
                    style={{ background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}`, color: ARCHITECT.onSurface }}
                  >
                    {VISIBILITY_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                data-testid="save-field-btn"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-black transition-all hover:brightness-110 disabled:opacity-60"
                style={{
                  background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`,
                  color: "#0e0e0e",
                }}
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                {editingField ? "Guardar cambios" : "Crear campo"}
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-3 rounded-xl text-sm font-semibold transition-all"
                style={{ background: ARCHITECT.surfaceHigh, color: ARCHITECT.onSurface }}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* System fields */}
      <div className="col-span-12">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3 px-2" style={{ color: ARCHITECT.primary }}>
          Campos del sistema
        </p>
        <BentoCard className="overflow-hidden p-0">
          {systemFields.map((field, idx) => (
            <FieldRow key={field.id} field={field} isLast={idx === systemFields.length - 1} isSystem />
          ))}
        </BentoCard>
      </div>

      {/* Custom fields */}
      <div className="col-span-12">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3 px-2" style={{ color: ARCHITECT.primary }}>
          Campos personalizados ({customFields.length})
        </p>
        {customFields.length === 0 ? (
          <div
            className="rounded-2xl p-12 text-center"
            style={{ background: ARCHITECT.surfaceLow, border: `1px dashed ${ARCHITECT.ghost}` }}
          >
            <Database size={32} className="mx-auto mb-3 opacity-30" style={{ color: ARCHITECT.onSurfaceVariant }} />
            <p className="text-sm font-semibold" style={{ color: ARCHITECT.onSurface }}>Sin campos personalizados aún</p>
            <p className="text-xs mt-1" style={{ color: ARCHITECT.onSurfaceVariant }}>Haz clic en "Nuevo campo" para comenzar</p>
          </div>
        ) : (
          <BentoCard className="overflow-hidden p-0">
            {customFields.map((field, idx) => (
              <FieldRow
                key={field.id}
                field={field}
                isLast={idx === customFields.length - 1}
                onEdit={() => openEdit(field)}
                onDelete={() => handleDelete(field)}
              />
            ))}
          </BentoCard>
        )}
      </div>
    </div>
  );
}

function FieldRow({ field, isLast, isSystem, onEdit, onDelete }) {
  const typeLabels = { text: "Texto", number: "Número", email: "Email", date: "Fecha", multivalue: "MultiValue" };
  const visLabels = { always: "Siempre", hide_empty: "Si tiene valor", never: "Nunca" };

  return (
    <div
      className="flex items-center gap-3 px-5 md:px-6 py-4"
      style={{ borderTop: !isLast ? "" : undefined, borderBottom: !isLast ? `1px solid ${ARCHITECT.ghost}` : "" }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold truncate" style={{ color: ARCHITECT.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {field.label}
          </span>
          {isSystem && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wider font-bold"
              style={{ color: ARCHITECT.onSurfaceVariant, background: ARCHITECT.surfaceLow }}
            >
              Sistema
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <code className="text-[10px] font-mono" style={{ color: ARCHITECT.primary }}>{field.field_id}</code>
          <span className="text-[10px]" style={{ color: `${ARCHITECT.onSurfaceVariant}60` }}>·</span>
          <span className="text-[10px]" style={{ color: ARCHITECT.onSurfaceVariant }}>{typeLabels[field.type] || field.type}</span>
          <span className="text-[10px]" style={{ color: `${ARCHITECT.onSurfaceVariant}60` }}>·</span>
          <span className="text-[10px]" style={{ color: ARCHITECT.onSurfaceVariant }}>{visLabels[field.visibility] || field.visibility}</span>
        </div>
        {field.description && (
          <p className="text-[11px] mt-1 truncate" style={{ color: `${ARCHITECT.onSurfaceVariant}90` }}>{field.description}</p>
        )}
      </div>
      {!isSystem && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            data-testid={`edit-field-${field.id}`}
            onClick={onEdit}
            className="p-2 rounded-lg transition-colors"
            style={{ color: ARCHITECT.onSurfaceVariant }}
            onMouseEnter={(e) => { e.currentTarget.style.background = ARCHITECT.surfaceLow; e.currentTarget.style.color = ARCHITECT.primary; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ARCHITECT.onSurfaceVariant; }}
          >
            <Edit3 size={13} />
          </button>
          <button
            data-testid={`delete-field-${field.id}`}
            onClick={onDelete}
            className="p-2 rounded-lg transition-colors"
            style={{ color: ARCHITECT.onSurfaceVariant }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,115,81,0.12)"; e.currentTarget.style.color = "#ff7351"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ARCHITECT.onSurfaceVariant; }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Client Domains Section ────────────────────────────────────────────────────

function ClientDomainsSection() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await authFetch(`${API}/admin/domains`).then(r => r.json());
      setDomains(d.domains || []);
    } catch (err) { console.error("[Settings] Domains load failed:", err); toast.error("Error cargando dominios"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (token) => {
    try {
      await authFetch(`${API}/admin/domains/${token}/verify`, { method: "PUT" });
      toast.success("Dominio marcado como verificado");
      load();
    } catch (err) { console.error("[Settings] Domain verify failed:", err); toast.error("Error"); }
  };

  const toggleFreeEligible = async (token, current) => {
    try {
      await authFetch(`${API}/admin/domains/${token}/free-eligible`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eligible: !current }),
      });
      toast.success(!current ? "Dominio gratis habilitado" : "Dominio gratis deshabilitado");
      load();
    } catch (err) { console.error("[Settings] Free eligible toggle failed:", err); toast.error("Error"); }
  };

  const removeDomain = async (token) => {
    if (!window.confirm("¿Eliminar el dominio de este cliente?")) return;
    try {
      await authFetch(`${API}/admin/domains/${token}/domain`, { method: "DELETE" });
      toast.success("Dominio eliminado");
      load();
    } catch (err) { console.error("[Settings] Remove domain failed:", err); toast.error("Error"); }
  };

  const withDomain = domains.filter(d => d.custom_domain);
  const verified = withDomain.filter(d => d.domain_verified).length;

  const filtered = filter === "all" ? domains
    : filter === "configured" ? withDomain
    : filter === "verified" ? domains.filter(d => d.domain_verified)
    : filter === "pending" ? domains.filter(d => d.custom_domain && !d.domain_verified)
    : domains;

  const SOURCE_LABEL = {
    namecheap_purchase: "Comprado vía Namecheap",
    namecheap_free: "Gratis vía Namecheap",
    manual: "Configurado manualmente",
  };

  return (
    <div className="grid grid-cols-12 gap-5 md:gap-6 pb-8">
      {/* Stats — Pulse Metrics */}
      {[
        { label: "Total clientes", value: domains.length, color: ARCHITECT.onSurface },
        { label: "Con dominio", value: withDomain.length, color: ARCHITECT.primary },
        { label: "Verificados", value: verified, color: "#22c55e" },
      ].map((stat) => (
        <BentoCard key={stat.label} className="col-span-12 md:col-span-4 p-6 md:p-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] mb-3" style={{ color: ARCHITECT.onSurfaceVariant }}>
            {stat.label}
          </p>
          <p
            className="text-5xl md:text-6xl font-black leading-none"
            style={{ color: stat.color, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            {stat.value}
          </p>
        </BentoCard>
      ))}

      {/* Filters */}
      <BentoCard className="col-span-12 p-4 md:p-5">
        <div className="flex gap-2 flex-wrap items-center">
          {[["all","Todos"],["configured","Con dominio"],["verified","Verificados"],["pending","Pendientes"]].map(([id, label]) => {
            const active = filter === id;
            return (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className="text-xs font-bold px-4 py-2 rounded-xl transition-all"
                style={active
                  ? { background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`, color: "#0e0e0e" }
                  : { background: ARCHITECT.surfaceLow, color: ARCHITECT.onSurfaceVariant, border: `1px solid ${ARCHITECT.ghost}` }}
              >
                {label}
              </button>
            );
          })}
          <button
            onClick={load}
            className="ml-auto text-xs font-bold flex items-center gap-1.5 px-3 py-2 rounded-xl transition-colors"
            style={{ color: ARCHITECT.onSurfaceVariant }}
          >
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>
      </BentoCard>

      {/* Table */}
      <div className="col-span-12">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin" style={{ color: ARCHITECT.primary }} />
          </div>
        ) : filtered.length === 0 ? (
          <BentoCard className="p-16 text-center">
            <Globe size={32} className="mx-auto mb-3 opacity-30" style={{ color: ARCHITECT.onSurfaceVariant }} />
            <p className="text-sm" style={{ color: ARCHITECT.onSurfaceVariant }}>Sin resultados</p>
          </BentoCard>
        ) : (
          <div className="space-y-3">
            {filtered.map(acc => (
              <BentoCard key={acc.token} className="p-5 md:p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <p className="text-base font-bold truncate" style={{ color: ARCHITECT.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        {acc.business_name || acc.email || "Cliente sin nombre"}
                      </p>
                      {acc.free_domain_eligible && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                          style={{ color: "#22c55e", background: "rgba(34,197,94,0.12)" }}>
                          Gratis habilitado
                        </span>
                      )}
                      {acc.free_domain_redeemed && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider"
                          style={{ color: ARCHITECT.primary, background: `${ARCHITECT.primary}15` }}>
                          Gratis canjeado
                        </span>
                      )}
                    </div>

                    {acc.custom_domain ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ background: acc.domain_verified ? "#22c55e" : "#eab308" }}
                        />
                        <code className="text-xs font-mono" style={{ color: ARCHITECT.primary }}>{acc.custom_domain}</code>
                        <span
                          className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                          style={acc.domain_verified
                            ? { color: "#22c55e", background: "rgba(34,197,94,0.1)" }
                            : { color: "#eab308", background: "rgba(234,179,8,0.1)" }}
                        >
                          {acc.domain_verified ? "Verificado" : "Pendiente"}
                        </span>
                        {acc.domain_source && (
                          <span className="text-[10px]" style={{ color: ARCHITECT.onSurfaceVariant }}>
                            {SOURCE_LABEL[acc.domain_source] || acc.domain_source}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs italic" style={{ color: `${ARCHITECT.onSurfaceVariant}80` }}>Sin dominio configurado</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
                    {acc.custom_domain && !acc.domain_verified && (
                      <button
                        onClick={() => verify(acc.token)}
                        className="text-[10px] font-black px-3 py-2 rounded-lg transition-all hover:brightness-110"
                        style={{
                          background: `linear-gradient(135deg, ${ARCHITECT.primaryDim}, ${ARCHITECT.primaryContainer})`,
                          color: "#0e0e0e",
                        }}
                      >
                        Verificar
                      </button>
                    )}
                    <button
                      onClick={() => toggleFreeEligible(acc.token, acc.free_domain_eligible)}
                      className="text-[10px] font-bold px-3 py-2 rounded-lg transition-all"
                      style={acc.free_domain_eligible
                        ? { color: "#22c55e", background: "rgba(34,197,94,0.08)", border: `1px solid rgba(34,197,94,0.25)` }
                        : { color: ARCHITECT.onSurfaceVariant, background: ARCHITECT.surfaceLow, border: `1px solid ${ARCHITECT.ghost}` }}
                    >
                      {acc.free_domain_eligible ? "Revocar gratis" : "Dar gratis"}
                    </button>
                    {acc.custom_domain && (
                      <button
                        onClick={() => removeDomain(acc.token)}
                        className="text-[10px] font-bold p-2 rounded-lg transition-all"
                        style={{ color: "#ff7351" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,115,81,0.1)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </BentoCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
